
import { v4 as uuidv4 } from "uuid";
import { 
  FeatureBase,
  OptionalFeatureBase,
} from "@models";
import { 
  CharacterASIBaseFeature,
  CharacterFeature,
  CharacterFeat,
  CharacterLanguageFeature,
  CharacterEldritchInvocation,
  CharacterFightingStyle,
  CharacterSpecialFeature,
  CharacterPactBoon,
  Character,
  CharacterProficiency,
  CharacterItem,
  CharacterSubrace,
  LanguageFeature,
  CharacterKnowledge,
  CharacterSubclass,
  SubclassFeature,
  SFFeature,
  SpellsFromList,
  Selectable,
  ComplexCreatureInstance,
  Summon,
} from "@dndModels";
import { 
  FeatureAncestry, 
  CharacterFeatureSource 
} from "@dndModels/character_feature_source/CharacterFeatureSource";

/**
 * This represents a feature 
 * (either via race, background, class, or subclass)
 * which the character has.  
 * The FeatureBase is what it is based on, but the 
 * difference is that here we will have values for the choices.
 */
export class CharacterFeatureBase {
  true_id = ""; // I should have named this feature_base_id and had cfb_true_id be true_id, but it's too late now.
  cfb_true_id = "";
  name = "";
  feature_base: FeatureBase | OptionalFeatureBase | null = null;
  get creature_fb_type() {
    return this.feature_base instanceof FeatureBase ? this.feature_base.creature_fb_type : "";
  }
  features: CharacterFeature[] = [];
  needs_attention = false;
  // source_true_id: string = "";
  _source_true_id = "";
  attached_source_true_id = "";
  source_id = "";
  source_type = "";
  source_name = "";
  parent_feature_base_level = -1;
  // If this is from a special feature, then root is the root source, otherwise it should be the same as source
  // root_id: string = "";
  // root_type: string = "";
  // root_name: string = "";
  ancestries: FeatureAncestry[] = [];
  item_id = "";
  item_name = "";
  class_id = "";
  replaces_cfb_on_source_id = "";
  replaces_cfb_on_source_type = "";
  replaces_cfb_on_source_name = "";
  replaces_cfb_on_item_id = "";
  replaces_cfb_on_item_name = "";
  true_level = -1;
  
  source_books: { type: string, id: string, third_party: boolean }[] = [];

  constructor(obj?: any, log_it = false) {
    this.cfb_true_id = obj && obj.cfb_true_id ? obj.cfb_true_id : uuidv4().toString();
    if (this.cfb_true_id === "") {
      this.cfb_true_id = uuidv4().toString();
    }
    if (obj) {
      this.true_id = obj.true_id ? obj.true_id : "";
      this.name = obj.name ? obj.name : "";
      // if (this.name === "Reaper") {
      //   debugger;
      // }
      // if (this.name === "Reaper") {
      //   console.log(this.parent_feature_base_level);
      //   console.log(obj.parent_feature_base_level);
      //   // debugger;
      // }
      // this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      this.source_id = obj.source_id ? obj.source_id : "";
      this.class_id = obj.class_id ? obj.class_id : "";
      this.features = [];
      if (obj.features && obj.features.length > 0) {
        for (let i = 0; i < obj.features.length; i++) {
          const f = new CharacterFeature(obj.features[i], log_it);
          f.cfb_id = this.true_id;
          f.cfb_name = this.name;
          this.features.push(f);
        }
      }
      this.attached_source_true_id = obj.attached_source_true_id ? obj.attached_source_true_id : "";
      this.feature_base = obj.feature_base ? new FeatureBase(obj.feature_base) : null;
      this.needs_attention = false;
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      cfb_true_id: this.cfb_true_id,
      name: this.name,
      features: this.features.map(o => o.toDBObj()),
      source_true_id: this.source_true_id,
      attached_source_true_id: this.attached_source_true_id,
      source_id: this.source_id,
      class_id: this.class_id,
      level: this.level, // This isn't used in loading for anything other than diagnostics
    };
  }

  get use_source_id(): string {
    if (this.replaces_cfb_on_source_id === "") {
      return this.source_id;
    } else {
      return this.replaces_cfb_on_source_id;
    }
  }

  get use_source_type(): string {
    if (this.replaces_cfb_on_source_type === "") {
      return this.source_type;
    } else {
      return this.replaces_cfb_on_source_type;
    }
  }

  get use_source_name(): string {
    if (this.replaces_cfb_on_source_name === "") {
      return this.source_name;
    } else {
      return this.replaces_cfb_on_source_name;
    }
  }

  get use_item_id(): string {
    if (this.replaces_cfb_on_item_id === "") {
      return this.item_id;
    } else {
      return this.replaces_cfb_on_item_id;
    }
  }

  get use_item_name(): string {
    if (this.replaces_cfb_on_item_name === "") {
      return this.item_name;
    } else {
      return this.replaces_cfb_on_item_name;
    }
  }

  get sort_level(): number {
    if (this.feature_base) {
      const levels = [this.feature_base.level, this.parent_feature_base_level, this.true_level];
      return Math.max(...levels);
    }
    return -1;
  }

  get position_at_level() {
    return this.feature_base?.position_at_level??-1;
  }

  get level(): number {
    if (this.true_level > -1) {
      return this.true_level;
    } else if (this.feature_base) {
      return this.feature_base.level;
    }
    return -1;
  }

  get for_class(): string {
    if (this.feature_base) {
      return this.feature_base.for_class;
    }
    return "Default";
  }

  get required_condition_ids() {
    if (this.feature_base) {
      return this.feature_base.required_condition_ids;
    }
    return [];
  }

  get condition_level() {
    if (this.feature_base) {
      return this.feature_base.condition_level;
    }
    return 1;
  }

  get exclude_conditions() {
    if (this.feature_base) {
      return this.feature_base.exclude_conditions;
    }
    return false;
  }

  get min_spell_slot_level() {
    if (this.feature_base) {
      return this.feature_base.min_spell_slot_level;
    }
    return 0;
  }

  get source_book_ids(): string[] {
    return this.source_books.map(o => o.id);
  }

  get source_book_types(): string[] {
    return this.source_books.map(o => o.type);
  }

  get third_party(): boolean {
    return this.source_books.filter(o => !o.third_party).length === 0;
  }

  get multiclassing(): number {
    if (this.feature_base) {
      return this.feature_base.multiclassing;
    }
    return -1;
  }

  get optional(): boolean {
    if (this.feature_base instanceof OptionalFeatureBase) {
      return true;
    }
    return false;
  }

  get replaces_feature_base_id(): string {
    if (this.feature_base) {
      return this.feature_base.replaces_feature_base_id;
    }
    return "";
  }

  get upgrades_feature_base_id(): string {
    if (this.feature_base) {
      return this.feature_base.upgrades_feature_base_id;
    }
    return "";
  }

  get key(): string {
    if (this.feature_base) {
      return this.feature_base.key;
    }
    return "";
  }

  get replacement_key(): string {
    if (this.feature_base) {
      return this.feature_base.replacement_key;
    }
    return "";
  }

  get replace_key(): boolean {
    if (this.feature_base) {
      return this.feature_base.replace_key;
    }
    return false;
  }

  get the_feature_base(): FeatureBase | null {
    if (this.feature_base instanceof OptionalFeatureBase) {
      return this.feature_base.feature_base;
    } else {
      return this.feature_base;
    }
  }

  // get edition_id(): string {
  //   if (this.feature_base) {
  //     return this.feature_base.edition_id;
  //   }
  //   return "";
  // }

  get display(): boolean {
    if (this.feature_base) {
      return this.feature_base.display;
    }
    return true;
  }

  get source_true_id() {
    // if (this.name === "Dread Strike") {
    //   console.log(this.ancestries);
    // }
    if (this._source_true_id === "") {
      const feat_ancestor = this.ancestries.find(o => o.type === "Feat");
      if (feat_ancestor) {
        return feat_ancestor.true_id;
      }
      return this.ancestries.length > 0 ? this.ancestries[0].true_id : "";
    }
    return this._source_true_id;
  }

  copyFeatureBase = (copyMe: FeatureBase | OptionalFeatureBase, 
    source: CharacterFeatureSource | Summon,
    char: Character | ComplexCreatureInstance | null = null, 
    log_it = false, repeat_level = -1) => {
    // if (copyMe.name.includes("Psionic Spells")) {
    //   debugger;
    // }
    // this.source_true_id = source.true_id;
    if (source instanceof Summon) {
      this.source_id = source.true_id;
      this.source_type = "Summon";
      this.source_name = source.name;
    } else if (source instanceof CharacterItem) {
      this.source_id = source.true_id;
      this.source_type = "Item";
      this.source_name = source.name;
      this.item_id = source.true_id;
      this.item_name = source.name;
    } else {
      this.source_id = source.source_id;
      this.source_type = source.source_type;
      this.source_name = source.name;
      this.item_id = source.item_id;
      this.item_name = source.item_name;
    }
    this.ancestries = source instanceof Summon ? [] : [...source.ancestries.map(o => { return {...o}; })];
    this.source_books = source instanceof Summon ? [] : source.source_books;

    this.feature_base = copyMe; // new FeatureBase(copyMe);
    this.true_id = copyMe instanceof FeatureBase ? copyMe.true_id : copyMe.feature_base.true_id;
    if (repeat_level > -1) {
      this.true_id += `_${repeat_level}`;
      this.true_level = repeat_level;
    }
    this.name = copyMe.fb_name;
    // if (this.name === "Reaper") {
    //   console.log(this.parent_feature_base_level);
    //   // console.log(copyMe.parent_feature_base_level);
    //   // debugger;
    // }
    if (log_it) {
      console.log(this);
      console.log(this.features);
    }
    const new_features: CharacterFeature[] = [];
    // New Sources is being moved to feature processing
    // const new_sources: CharacterFeatureSource[] = [];
    for (const f of copyMe.features) {
      const f_true_id = repeat_level > -1 ? `${f.true_id}_${repeat_level}` : f.true_id;
      let char_feature = new CharacterFeature();
      const cf_finder = this.features.filter(o => 
        o.feature_id === f_true_id);
      if (log_it) {
        console.log(f);
        console.log(cf_finder);
        if (cf_finder.length === 1) {
          for (const fo of cf_finder[0].feature_options) {
            if (fo instanceof CharacterASIBaseFeature) {
              for (const asif of fo.asi_features) {
                console.log(asif.selected_option);
              }
            } else if (fo instanceof CharacterSpecialFeature) {
              console.log(fo);
            }
          }
        }
      }
      if (cf_finder.length === 1) {
        char_feature = cf_finder[0];
      }
      // char_feature is empty.  
      // We need to check for existing ones and copy them if necessary.
      char_feature.copyFeature(f, char, this, log_it, repeat_level);
      char_feature.cfb_id = this.true_id;
      char_feature.cfb_name = this.name;
      new_features.push(char_feature);
      if (log_it) {
        console.log(f);
        console.log(char_feature);
        for (const fo of char_feature.feature_options) {
          if (fo instanceof CharacterASIBaseFeature) {
            for (const asif of fo.asi_features) {
              console.log(asif.selected_option);
            }
          } else if (fo instanceof CharacterSpecialFeature) {
            console.log(fo);
          }
        }
      }
    }
    this.features = new_features;
    this.calcAttention();
  }

  calcAttention(log_it = false) {
    this.needs_attention = false;
    // if (this.name.includes("Elemental")) {
    //   debugger;
    // }
    for (let i = 0; i < this.features.length; i++) {
      const cf = this.features[i];
      if (cf.feature) { 
        if (["Proficiencies","Expertise"].includes(cf.feature_type)) {
          for (let j = 0; j < cf.feature_options.length; j++) {
            const opt = cf.feature_options[j] as CharacterProficiency;
            if (["Gaming Set", "Artisan's Tools", "Musical Instrument", "Specialty Tool", "Other Tool", "Any"].includes(opt.proficiency_id)) {
              opt.proficiency_id = "";
            }
            if (opt.proficiency_id === "") {
              this.needs_attention = true;
              break;
            }
          }
        } else if (cf.feature_type === "Language") {
          const opt = cf.feature_options[0] as CharacterLanguageFeature;
          const lf = cf.floating_feature as LanguageFeature;
          // if (feature.true_id === "a81481e6-7602-40c2-a207-9550abf13acd") {
          //   console.log(opt);
          //   console.log(lf);
          //   debugger;
          // }
          if (opt.language_ids.length !== lf.count) {
            // if (lf.language_ids.length === lf.count && !lf.language_ids.includes("Any")) {
            //   opt.language_ids = lf.language_ids; // I believe this is redundant
            // } else {
            this.needs_attention = true;
            break;
            // }
          }
        } else if (cf.feature_type === "Ability Score Improvement") {
          const opt = cf.feature_options[0] as CharacterASIBaseFeature;
          // debugger;
          if (opt.feat_option && opt.use_feat === null) {
            this.needs_attention = true;
            break;
          } else if (opt.feat_option && opt.use_feat) {
            if (opt.feat.source_id === "") {
              this.needs_attention = true;
              break;
            }
          } else {
            if (opt.asi_base_feature && opt.asi_base_feature.choices.length > 1 && opt.selected_choice_id === "") {
              this.needs_attention = true;
            } else {
              for (let k = 0; k < opt.asi_features.length; k++) {
                const asi = opt.asi_features[k];
                if (asi.selected_option === "None") {
                  this.needs_attention = true;
                  break;
                }
              }
              if (this.needs_attention) {
                break;
              }
            }
          }
        } else if (cf.feature_type === "Feat") {
          const feat: CharacterFeat = cf.feature_options[0];
          if (feat.source_id === "") {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Selectable") {
          const selectable = cf.floating_feature as Selectable;
          if (selectable.type === "Spell" || selectable.type === "Item") {
            // debugger;
            if (cf.feature_options.filter(o => o === "").length > 0) {
              this.needs_attention = true;
              break;
            }
          } else {
            const opt = cf.feature_options[0] as string;
            if (opt === "") {
              this.needs_attention = true;
              break;
            }
          }
        } else if (cf.feature_type === "Eldritch Invocations") {
          const eldritch_invocation = (cf.feature_options as CharacterEldritchInvocation[]).find(o => o.source_id === "");
          if (eldritch_invocation) {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Weapon Mastery") {
          // console.log(cf.feature_options);
          if (cf.feature_options.length === 0) {
            this.needs_attention = true;
            break;
          }
          // if (typeof cf.feature_options[0] !== "string") {
          //   console.log(cf.feature_options);
          // }
          const wm = (cf.feature_options as string[]).find(o => o === "");
          if (wm !== undefined) {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Fighting Style") {
          const fighting_style = cf.feature_options[0] as CharacterFightingStyle;
          if (fighting_style.source_id === "") {
            this.needs_attention = true;
            break;
          }
        } else if (["Cantrips from List", "Spells from List", 
          "Cantrips From List", "Spells From List"].includes(cf.feature_type)) {
          const sfl = cf.floating_feature as SpellsFromList;
          if (sfl.spell_limit.use_selectable === false) {
            const ids = cf.feature_options as string[];
            if (ids.filter(o => o === "").length > 0) {
              this.needs_attention = true;
              break;
            }
          }
        } else if (cf.feature_type.includes("Special Feature") && cf.feature_type !== "Special Feature List Merge") {
          // debugger;
          // if (this.name.includes("Divine Order")) {
          //   console.log(this);
          //   debugger;
          // }
          if (cf.floating_feature instanceof SFFeature) {
            if (!cf.floating_feature.allow_none) {
              if (cf.floating_feature.special_feature_ids.length !== cf.feature_options.length) {
                // needs to go through all the options like this, and through the features on each
                for (let l = 0; l < cf.feature_options.length; ++l) {
                  const special_feature = cf.feature_options[l] as CharacterSpecialFeature;
                  // if (this.name.includes("Elemental")) {
                  //   console.log(special_feature);
                  // }
                  if (special_feature.source_id === "") {
                    this.needs_attention = true;
                    break;
                  }
                }
              }
            }
          }
          if (this.needs_attention) {
            break;
          }
        } else if (cf.feature_type.includes("Condition")) {
          // No need to do anything here
        } else if (cf.feature_type === "Pact Boon") {
          const pact_boon = cf.feature_options[0] as CharacterPactBoon;
          if (pact_boon.source_id === "") {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Subclass") {
          // const char_class = char.spirit.classes.find(o => o.source_id === feature.root_id);
          // if (char_class) {
          //  this.needs_attention = char_class.subclass_id === "";
          //   break;
          // }
          if (cf.floating_feature instanceof SubclassFeature && 
            cf.floating_feature.level === 1) {
            const char_subclass = cf.feature_options[0];
            if (char_subclass === null || (char_subclass as CharacterSubclass).source_id === "") {
              this.needs_attention = true;
              break;
            }
          }
        } else if (cf.feature_type === "Subrace") {
          const char_subrace = cf.feature_options[0];
          if (char_subrace === null || (char_subrace as CharacterSubrace).source_id === "") {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Characteristic") {
          const characteristic = cf.feature_options[0];
          if (characteristic === "") {
            this.needs_attention = true;
            break;
          }
        } else if (cf.feature_type === "Knowledge") {
          const char_knowledge = (cf.feature_options as CharacterKnowledge[]).find(o => o.obj_id === "");
          if (char_knowledge) {
            this.needs_attention = true;
            break;
          }
        } else {
          for (let j = 0; j < cf.feature_options.length; j++) {
            const opt: any = cf.feature_options[j];
            if (opt === "") {
              this.needs_attention = true;
              break;
            }
          }
        }
      }
      if (this.needs_attention) {
        break;
      }
    }
    if (log_it && this.needs_attention) {
      console.log(this);
    }
  }

  clone(drop_ids = false): CharacterFeatureBase {
    const clone = new CharacterFeatureBase();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterFeatureBase, drop_ids = false): void {
    if (!drop_ids) {
      this.cfb_true_id = copyMe.cfb_true_id;
      this.true_id = copyMe.true_id;
    }
    this.source_id = copyMe.source_id;
    this.source_type = copyMe.source_type;
    this.source_name = copyMe.source_name;
    this.attached_source_true_id = copyMe.attached_source_true_id;
    // if (this.name === "Reaper") {
    //   console.log(this.parent_feature_base_level);
    //   console.log(copyMe.parent_feature_base_level);
    //   // debugger;
    // }
    this.parent_feature_base_level = copyMe.parent_feature_base_level;
    this._source_true_id = copyMe._source_true_id;
    this.true_level = copyMe.true_level;
    this.replaces_cfb_on_source_id = copyMe.replaces_cfb_on_source_id;
    this.replaces_cfb_on_source_type = copyMe.replaces_cfb_on_source_type;
    this.replaces_cfb_on_source_name = copyMe.replaces_cfb_on_source_name;
    this.replaces_cfb_on_item_id = copyMe.replaces_cfb_on_item_id;
    this.replaces_cfb_on_item_name = copyMe.replaces_cfb_on_item_name;
    
    this.ancestries = [...copyMe.ancestries.map(o => { return {...o}; })];

    // this.root_id = copyMe.root_id;
    // this.root_type = copyMe.root_type;
    // this.root_name = copyMe.root_name;
    this.item_id = copyMe.item_id;
    this.item_name = copyMe.item_name;
    this.name = copyMe.name;
    this.feature_base = copyMe.feature_base;
    this.needs_attention = copyMe.needs_attention;
    this.class_id = copyMe.class_id;
    this.features = [...copyMe.features];
    // this.features = copyMe.features.map(o => {
    //   const cf = o.clone();
    //   cf.cfb_id = this.true_id;
    //   cf.cfb_name = this.name;
    //   return cf;
    // });
  }
}